import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import PostJob from "@/pages/PostJob";
import MyJobs from "@/pages/MyJobs";
import JobDetail from "@/pages/JobDetail";
import OpenJobs from "@/pages/OpenJobs";
import MyBids from "@/pages/MyBids";
import MyWork from "@/pages/MyWork";
import ReviewerPool from "@/pages/ReviewerPool";
import ReviewDetail from "@/pages/ReviewDetail";
import AdminHome from "@/pages/AdminHome";
import Profile from "@/pages/Profile";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.onboarded && window.location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="display-serif text-3xl text-zinc-500">…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check for OAuth callback fragment — must happen BEFORE ProtectedRoute checks.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/post-job" element={<Protected><PostJob /></Protected>} />
      <Route path="/my-jobs" element={<Protected><MyJobs /></Protected>} />
      <Route path="/jobs/:jobId" element={<Protected><JobDetail /></Protected>} />
      <Route path="/open-jobs" element={<Protected><OpenJobs /></Protected>} />
      <Route path="/my-bids" element={<Protected><MyBids /></Protected>} />
      <Route path="/my-work" element={<Protected><MyWork /></Protected>} />
      <Route path="/review-pool" element={<Protected><ReviewerPool /></Protected>} />
      <Route path="/reviews/:jobId" element={<Protected><ReviewDetail /></Protected>} />
      <Route path="/admin" element={<Protected><AdminHome /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Show landing if not authenticated; dashboard if authenticated.
function PublicHome() {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (user) return <Navigate to={user.onboarded ? "/dashboard" : "/onboarding"} replace />;
  return <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
